import { Alert, Box, IconButton, Stack, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { Pencil as Edit, Plus, Trash2 } from "lucide-react";
import { Helmet } from "react-helmet";
import { useParams } from "react-router-dom";

import { getSingleCustomer } from "api/customers";
import { customersKeys } from "api/customers/queries";
import { CustomersType } from "api/customers/types";
import PageLayout from "components/layouts/PageLayout";
import { hasPermissions } from "components/stores/UserStore";
import UltimateButton from "components/UltimateButton";
import EditHubspotContactIdModal from "containers/creditEvaluations/details/components/EditHubspotContactId/EditHubspotContactIdModal";
import { useModal } from "utils/hooks/useModal";

import AddSocialNumberModal from "./components/AddSocialNumberModal";
import CustomerCreditEvaluationsTable from "./components/CustomerCreditEvaluationsTable";
import CustomerOverview from "./components/CustomerOverview";
import UpdateGeneralInfoModal from "./components/UpdateGeneralInfoModal";
import DeleteCustomerModal from "../CustomersTable/components/DeleteCustomerModal";
import EditCustomerModal from "../CustomersTable/components/EditCustomerModal";

const customerDisplayName = (c: CustomersType | undefined) =>
  [c?.firstName, c?.middleName, c?.lastName].filter(Boolean).join(" ") ||
  "Customer";

const SingleCustomerPage = () => {
  const { customerId } = useParams<{ customerId: string }>();

  const {
    isOpen: isAddSocialNumberOpen,
    handleClose: handleCloseAddSocialNumber,
    handleOpen: handleOpenAddSocialNumber,
  } = useModal<CustomersType>();

  const {
    isOpen: isUpdateGeneralOpen,
    handleClose: handleCloseUpdateGeneral,
    handleOpen: handleOpenUpdateGeneral,
  } = useModal<CustomersType>();

  const {
    isOpen: isEditOpen,
    handleClose: handleCloseEdit,
    handleOpen: handleOpenEdit,
    context: editContext,
  } = useModal<CustomersType>();

  const {
    isOpen: isOpenDelete,
    handleClose: handleCloseDelete,
    handleOpen: handleOpenDelete,
    context: deleteContext,
  } = useModal<CustomersType>();

  const { data: customer, isLoading: isCustomerLoading } = useQuery(
    customersKeys.details(customerId),
    async () => {
      const { data: res } = await getSingleCustomer(customerId);
      return res.data;
    },
  );

  const {
    isOpen: isOpenEditHubspotContactId,
    handleClose: handleCloseEditHubspotContactId,
    handleOpen: handleOpenEditHubspotContactId,
  } = useModal<{}>();

  const title = customer ? customerDisplayName(customer) : "Customer details";
  const subtitle =
    "View and manage customer information and credit evaluations.";

  const actions = (
    <Stack
      direction="row"
      alignItems="center"
      spacing={{ xs: 1, sm: 2 }}
      flexWrap="wrap"
      gap={1}
    >
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Typography variant="textSM" color="text.secondary" noWrap>
          Hubspot Contact ID:
        </Typography>
        <Typography variant="textSM" fontWeight={600} component="span" noWrap>
          {customer?.hubspotId ?? "—"}
        </Typography>
        <IconButton
          color="primary"
          size="small"
          onClick={() => handleOpenEditHubspotContactId()}
          aria-label="Edit Hubspot Contact ID"
        >
          <Edit size={18} />
        </IconButton>
      </Stack>
      {hasPermissions("update:customers") && (
        <UltimateButton
          text="Edit Customer"
          Icon={
            Edit as React.ComponentType<{
              size?: number;
              className?: string;
              style?: React.CSSProperties;
            }>
          }
          onClick={() => handleOpenEdit(customer)}
        />
      )}
      {hasPermissions("delete:customers") && (
        <UltimateButton
          text="Delete Customer"
          color="brandDanger"
          Icon={
            Trash2 as React.ComponentType<{
              size?: number;
              className?: string;
              style?: React.CSSProperties;
            }>
          }
          onClick={() => handleOpenDelete(customer)}
        />
      )}
    </Stack>
  );

  return (
    <>
      <Helmet
        title={`Lendzee | ${
          customer ? customerDisplayName(customer) : "Customer"
        } ${customer?.email ?? ""}`}
      />

      <PageLayout title={title} subtitle={subtitle} actions={actions}>
        <Stack
          spacing={3}
          flex={1}
          height="100%"
          overflow="scroll"
          className="scrollbar-hidden"
        >
          {Boolean(customer?.cbcErrorMessage) && (
            <Alert severity="error" color="error">
              <Typography variant="labelMD" fontWeight={600}>
                CBC Error Message
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {customer?.cbcErrorMessage}
              </Typography>
              {hasPermissions("update:customers") && (
                <Box sx={{ mt: 1.5 }}>
                  {customer?.cbcErrorMessage?.includes("SS# REQUIRED") ? (
                    <UltimateButton
                      text="Add Social Number"
                      Icon={
                        Plus as React.ComponentType<{
                          size?: number;
                          className?: string;
                          style?: React.CSSProperties;
                        }>
                      }
                      onClick={() => handleOpenAddSocialNumber()}
                    />
                  ) : (
                    <UltimateButton
                      text="Update General Info"
                      Icon={
                        Edit as React.ComponentType<{
                          size?: number;
                          className?: string;
                          style?: React.CSSProperties;
                        }>
                      }
                      onClick={() => handleOpenUpdateGeneral()}
                    />
                  )}
                </Box>
              )}
            </Alert>
          )}

          <CustomerOverview customer={customer} isLoading={isCustomerLoading} />

          <Box>
            <Typography variant="headingXS" fontWeight={600} sx={{ mb: 2 }}>
              Credit Evaluations
            </Typography>
            <CustomerCreditEvaluationsTable customerId={customerId} />
          </Box>
        </Stack>
      </PageLayout>

      <AddSocialNumberModal
        isOpen={isAddSocialNumberOpen}
        handleClose={handleCloseAddSocialNumber}
        customerId={customerId}
      />
      {customer && (
        <UpdateGeneralInfoModal
          isOpen={isUpdateGeneralOpen}
          handleClose={handleCloseUpdateGeneral}
          customer={customer}
        />
      )}

      {hasPermissions("update:customers") && (
        <>
          <EditCustomerModal
            isOpen={isEditOpen}
            handleClose={handleCloseEdit}
            customer={editContext}
          />
          <EditHubspotContactIdModal
            customerId={customerId}
            hubspotContactId={customer?.hubspotId!}
            isOpen={isOpenEditHubspotContactId}
            handleClose={handleCloseEditHubspotContactId}
          />
        </>
      )}

      {hasPermissions("delete:customers") && (
        <DeleteCustomerModal
          isOpen={isOpenDelete}
          handleClose={handleCloseDelete}
          customer={deleteContext}
        />
      )}
    </>
  );
};

export default SingleCustomerPage;
